import { type CSSProperties, type FC, useMemo } from 'react'
import styled, { keyframes } from 'styled-components'

const COLORS = ['#d4a843', '#c9973b', '#e6c36a', '#b8862d', '#f0d98c', '#dbb54e', '#c4a24a']
const TOTAL_PARTICLES = 150
const SPAWN_DURATION_S = 2.5

const random = (min: number, max: number) => min + Math.random() * (max - min)
const pick = <T,>(items: readonly T[]): T => items[Math.floor(Math.random() * items.length)] as T

const buildPieces = () =>
  Array.from({ length: TOTAL_PARTICLES }, () => {
    const depth = Math.random()
    const width = random(12, 28)
    const scale = 0.4 + depth * 0.8
    return {
      left: Math.random() * 100,
      width,
      height: width * random(0.3, 0.45),
      color: pick(COLORS),
      drift: random(-150, 150),
      rotStart: random(0, 720),
      rotEnd: random(-15, 15),
      fallDuration: random(1, 2.5),
      delay: random(0, SPAWN_DURATION_S),
      endY: 62 + depth * 36,
      endScale: scale,
      endScaleY: scale * 0.35,
      endOpacity: 0.35 + depth * 0.5,
    }
  })

const confettiFall = keyframes`
  0% {
    transform: translateY(-20px) translateX(0) rotate(var(--rot-start)) scale(0.3, 1);
    opacity: 1;
  }
  60% { opacity: 1; }
  85% {
    transform: translateY(var(--end-y)) translateX(var(--drift)) rotate(var(--rot-end)) scale(var(--end-scale), var(--end-scale-y));
    opacity: var(--end-opacity);
  }
  100% {
    transform: translateY(var(--end-y)) translateX(var(--drift)) rotate(var(--rot-end)) scale(var(--end-scale), var(--end-scale-y));
    opacity: var(--end-opacity);
  }
`

const Container = styled.div`
  position: fixed;
  inset: 0;
  width: 100vw;
  height: 100vh;
  z-index: 5;
  pointer-events: none;
  overflow: hidden;
`

const Piece = styled.div<{ $duration: number; $delay: number }>`
  position: absolute;
  top: 0;
  border-radius: 50%;
  opacity: 0;
  pointer-events: none;
  animation-name: ${confettiFall};
  animation-duration: ${(p) => p.$duration}s;
  animation-delay: ${(p) => p.$delay}s;
  animation-timing-function: ease-in;
  animation-fill-mode: forwards;
  animation-iteration-count: 1;
`

export const Confetti: FC = () => {
  const prefersReducedMotion = useMemo(
    () => typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches,
    [],
  )
  const pieces = useMemo(buildPieces, [])

  if (prefersReducedMotion) {
    return null
  }

  return (
    <Container aria-hidden>
      {pieces.map((p, i) => (
        <Piece
          // biome-ignore lint/suspicious/noArrayIndexKey: static particles for the lifetime of this component
          key={i}
          $duration={p.fallDuration}
          $delay={p.delay}
          style={
            {
              left: `${p.left}%`,
              width: `${p.width}px`,
              height: `${p.height}px`,
              background: p.color,
              '--rot-start': `${p.rotStart}deg`,
              '--rot-end': `${p.rotEnd}deg`,
              '--drift': `${p.drift}px`,
              '--end-y': `${p.endY}vh`,
              '--end-scale': p.endScale,
              '--end-scale-y': p.endScaleY,
              '--end-opacity': p.endOpacity,
            } as CSSProperties
          }
        />
      ))}
    </Container>
  )
}
